import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import publicAxios from '../../utils/publicAxios';
import AuthContext from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import LogoImage from '../common/LogoImage';

const PaymentSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('Thank you for your payment! We\'re finalizing your account setup...');
  const [status, setStatus] = useState('pending');
  const [progress, setProgress] = useState(25); // Start at 25% (payment done)
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated, setUser } = useContext(AuthContext);
  
  // Poll for status updates
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const token = localStorage.getItem('pendingRegistrationToken');
    const email = localStorage.getItem('pendingRegistrationEmail');
    
    console.log('Payment success page loaded', { token, email, sessionId });
    
    let pollingInterval;
    let attempts = 0;
    const maxAttempts = 30; // 5 minutes (10 seconds * 30)
    
    const checkStatus = async () => {
      try {
        // First try the new status check endpoint
        try {
          const response = await publicAxios.post('/api/pending-registrations/check-status', {
            sessionId,
            token,
            email
          });
          
          console.log('Status check response:', response.data);
          
          setStatus(response.data.status);
          setMessage(response.data.message);
          
          // Update progress based on status
          switch(response.data.status) {
            case 'pending':
              setProgress(25);
              break;
            case 'payment_initiated':
              setProgress(40);
              break;
            case 'payment_received':
              setProgress(60);
              break;
            case 'account_created':
            case 'account_exists':
              setProgress(80);
              break;
            case 'completed':
              setProgress(100);
              break;
            default:
              setProgress(25);
          }
          
          // If account is created, complete login
          if (response.data.status === 'account_created' || response.data.status === 'account_exists') {
            if (response.data.token) {
              // Auto-login with provided token
              localStorage.setItem('token', response.data.token);
              setIsAuthenticated(true);
              
              // Get user data
              const userRes = await axios.get('/api/auth');
              setUser(userRes.data);
              
              // Clear registration data
              localStorage.removeItem('pendingRegistrationToken');
              localStorage.removeItem('pendingRegistrationEmail');
              localStorage.removeItem('pendingRegistrationPassword');
              
              // Stop polling
              clearInterval(pollingInterval);
              setProgress(100);
              
              // Redirect to dashboard after a short delay
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
              return;
            }
          }
          
          // Continue polling if not complete
          return;
        } catch (statusErr) {
          console.log('Status check failed, trying fallback methods:', statusErr);
          // Continue with fallback methods
        }
        
        // Fallback: Try to recover by checking if the user exists
        if (email) {
          try {
            // Try to log in with the stored email
            const loginResponse = await publicAxios.post('/api/auth/login', { 
              email, 
              password: localStorage.getItem('pendingRegistrationPassword') 
            });
            
            // If login succeeds, the user was created by the webhook
            localStorage.setItem('token', loginResponse.data.token);
            setIsAuthenticated(true);
            
            // Get user data
            const userRes = await axios.get('/api/auth');
            setUser(userRes.data);
            
            // Clear registration data
            localStorage.removeItem('pendingRegistrationEmail');
            localStorage.removeItem('pendingRegistrationPassword');
            
            // Stop polling
            clearInterval(pollingInterval);
            setProgress(100);
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            return;
          } catch (loginErr) {
            console.log('Auto-login failed, continuing with verification');
          }
        }
        
        // Fallback: If we have a session ID but no token, try to find the registration by session ID
        if (sessionId && !token) {
          try {
            const sessionResponse = await publicAxios.post('/api/pending-registrations/find-by-session', { 
              sessionId 
            });
            
            if (sessionResponse.data.token) {
              // Found a registration by session ID
              const foundToken = sessionResponse.data.token;
              localStorage.setItem('pendingRegistrationToken', foundToken);
              
              // Try to verify with this token
              const verifyResponse = await publicAxios.post('/api/pending-registrations/verify', { token: foundToken });
              
              // Clear the registration data
              localStorage.removeItem('pendingRegistrationToken');
              localStorage.removeItem('pendingRegistrationEmail');
              localStorage.removeItem('pendingRegistrationPassword');
              
              // Set authentication
              localStorage.setItem('token', verifyResponse.data.token);
              setIsAuthenticated(true);
              
              // Get user data
              const userRes = await axios.get('/api/auth');
              setUser(userRes.data);
              
              // Stop polling
              clearInterval(pollingInterval);
              setProgress(100);
              
              // Redirect to dashboard after a short delay
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
              return;
            }
          } catch (sessionErr) {
            console.log('Failed to find or verify registration by session ID');
          }
        }
        
        // Fallback: Try to verify with token if we have one
        if (token) {
          try {
            // Verify the registration
            const response = await publicAxios.post('/api/pending-registrations/verify', { token });
            
            // Clear the registration data
            localStorage.removeItem('pendingRegistrationToken');
            localStorage.removeItem('pendingRegistrationEmail');
            localStorage.removeItem('pendingRegistrationPassword');
            
            // Set authentication
            localStorage.setItem('token', response.data.token);
            setIsAuthenticated(true);
            
            // Get user data
            const userRes = await axios.get('/api/auth');
            setUser(userRes.data);
            
            // Stop polling
            clearInterval(pollingInterval);
            setProgress(100);
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
            return;
          } catch (verifyErr) {
            console.log('Verification failed:', verifyErr);
          }
        }
        
        // Stop polling after max attempts
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(pollingInterval);
          setError('Your account is taking longer than expected to activate. Please try logging in with your email and password, or contact support.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Status check error:', err);
        
        // Stop polling after max attempts
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(pollingInterval);
          setError('Failed to verify registration status. Please try logging in with your email and password, or contact support.');
          setLoading(false);
        }
      }
    };
    
    // Initial check
    checkStatus();
    
    // Start polling every 10 seconds
    pollingInterval = setInterval(checkStatus, 10000);
    
    // Cleanup
    return () => {
      clearInterval(pollingInterval);
    };
  }, [navigate, location, setIsAuthenticated, setUser]);
  
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      maxWidth: '600px',
      margin: '0 auto',
      marginTop: '2rem',
      backgroundColor: '#fff',
      borderRadius: '5px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '2rem',
      marginBottom: '1rem',
      color: '#243444'
    },
    message: {
      fontSize: '1.2rem',
      marginBottom: '1.5rem',
      textAlign: 'center',
      color: '#4C5454'
    },
    error: {
      color: '#7C1227',
      marginBottom: '1.5rem',
      textAlign: 'center',
      fontSize: '1.1rem'
    },
    spinner: {
      marginBottom: '1.5rem',
      color: '#D2BA7B'
    },
    progressContainer: {
      width: '100%',
      marginBottom: '2rem',
      backgroundColor: '#f0f0f0',
      borderRadius: '10px',
      overflow: 'hidden'
    },
    progressBar: {
      height: '10px',
      backgroundColor: '#D2BA7B',
      transition: 'width 0.5s ease-in-out'
    },
    statusContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      color: '#4C5454'
    },
    statusItem: {
      textAlign: 'center',
      position: 'relative'
    },
    statusItemActive: {
      fontWeight: 'bold',
      color: '#243444'
    },
    statusItemComplete: {
      fontWeight: 'bold',
      color: '#D2BA7B'
    },
    loginLink: {
      marginTop: '1rem',
      color: '#243444',
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  };

  // Helper function to determine status item style
  const getStatusItemStyle = (itemProgress) => {
    if (progress >= 100) return styles.statusItemComplete;
    return progress >= itemProgress ? styles.statusItemActive : {};
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Payment Successful!</h1>

      {/* Progress bar */}
      <div style={styles.progressContainer}>
        <div style={{ ...styles.progressBar, width: `${progress}%` }}></div>
      </div>

      {/* Status indicators */}
      <div style={styles.statusContainer}>
        <div style={{ ...styles.statusItem, ...getStatusItemStyle(25) }}>
          Payment
        </div>
        <div style={{ ...styles.statusItem, ...getStatusItemStyle(60) }}>
          Processing
        </div>
        <div style={{ ...styles.statusItem, ...getStatusItemStyle(80) }}>
          Account Setup
        </div>
        <div style={{ ...styles.statusItem, ...getStatusItemStyle(100) }}>
          Complete
        </div>
      </div>

      {loading && progress < 100 ? (
        <>
          <div style={styles.spinner}>
            <LoadingSpinner size="medium" color="#243444" />
          </div>
          <p style={styles.message}>
            {message || 'Completing your registration...'}
          </p>
          {progress >= 60 && (
            <p style={{ fontSize: '0.9rem', color: '#4C5454', textAlign: 'center' }}>
              This may take a moment. We're setting up your account...
            </p>
          )}
        </>
      ) : error ? (
        <>
          <div style={styles.error}>{error}</div>
          <p style={{ fontSize: '0.9rem', marginTop: '1rem', textAlign: 'center' }}>
            If you've completed payment but are seeing this error, please try to{' '}
            <Link to="/login" style={{ color: '#243444', fontWeight: 'bold' }}>
              log in
            </Link>{' '}
            with your email and password or contact support.
          </p>
        </>
      ) : (
        <p style={styles.message}>
          Registration completed successfully! Redirecting to dashboard...
        </p>
      )}
    </div>
  );
};

export default PaymentSuccess;
